import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { routerConst } from '@ewf/lib/router';
import { useUnstakingStore } from '@ewf/stores/unstaking';
import { useNotificationStore } from '@ewf/stores/notifications';
import { useWalletEnvStore } from '@ewf/stores/wallet-env';
import { useConnectionStore } from '@ewf/stores/connection';
import * as Dialog from '@ewf/components/Dialog';
import { TxSuccess } from '@ewf/components/TxSuccess';
import { TxFailure } from '@ewf/components/TxFailure';
import { TxExecuting } from '@ewf/components/TxExecuting';
import { ClaimReward } from '@ewf/components/ClaimReward';
import { Confirm } from './Confirm';
import { formatPalletErrorMessage, getNativeTokenSymbol } from '@ewf/lib/utils';
import { convertRewardAmount } from '@ewf/utils/balance-helpers';
import { ConnectEWXWallet } from '../Staking/ConnectEWXWallet/ConnectEWXWallet';
import { PromptUnsubscription } from './PromptUnsubscription';
import { PromptClaimReward } from './PromptClaimReward';
import { useApiServiceStore } from '@ewf/stores/api';
import { WorkerApi } from '@shared/types/api';
import { isElectron } from '@shared/utils/is-electron';

type Step =
  | 'promptUnsubscription'
  | 'promptClaimReward'
  | 'confirm'
  | 'connect'
  | 'executing'
  | 'success'
  | 'failure';

export const Unstaking = ({
  workerId,
  namespace,
  withdrawalDelay = 0,
  isOpen,
  setIsOpen,
  onFinishUnsubscriptionDelay,
  stakedAmount = '0',
}: Props): React.ReactNode => {
  const workerApi = useApiServiceStore.getState().api;
  const [step, setStep] = useState<Step>('promptUnsubscription');
  const [errorMsg, setErrorMsg] = useState('');
  const [fee, setFee] = useState(0.0);
  const [txUrl, setTxUrl] = useState('');
  const [isClaimRewardOpen, setIsClaimRewardOpen] = useState(false);
  const [pendingRewards, setPendingRewards] = useState<{
    amount: string;
    symbol: string;
    decimals: number;
    isNative: boolean;
  }>({
    amount: '0',
    symbol: 'EWT',
    decimals: 18,
    isNative: true,
  });
  const isConnected = useConnectionStore((state) => state.isConnected);
  const addressEWX = useConnectionStore((state) => state.addressEWX);
  const walletConst = useWalletEnvStore((state) => state.walletConst);
  const status = useUnstakingStore((state) => state.status);
  const unstake = useUnstakingStore((state) => state.unstake);
  const reset = useUnstakingStore((state) => state.reset);
  const unstakeSuccessNotification = useNotificationStore(
    (state) => state.unstakeSuccessNotification,
  );
  const unstakeErrorNotification = useNotificationStore((state) => state.unstakeErrorNotification);
  const unsubscriptionDelaySuccessNotification = useNotificationStore(
    (state) => state.unsubscriptionDelaySuccessNotification,
  );
  const navigate = useNavigate();

  // Steps transitions. Simulates a finite state machine, but only with the transitions.
  const steps = {
    promptUnsubscription: {
      promptClaimReward: 'promptClaimReward',
      confirm: 'confirm',
    },
    promptClaimReward: {
      confirm: 'confirm',
    },
    confirm: {
      promptUnsubscription: 'promptUnsubscription',
      connect: 'connect',
      executing: 'executing',
    },
    connect: {
      executing: 'executing',
    },
    executing: {
      success: 'success',
      failure: 'failure',
    },
    success: {},
    failure: {},
  } as const satisfies Record<Step, Partial<Record<Step, Step>>>;

  const onClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      if (withdrawalDelay === 0 && pendingRewards.amount !== '0') {
        setStep('promptClaimReward');
      } else {
        setStep('promptUnsubscription');
      }
      reset();
      setTxUrl('');
    }, 300);
  };

  const onConfirm = () => {
    if (isConnected) {
      proceedUnstake();
    } else {
      setStep(steps.confirm.connect);
    }
  };

  const onAcceptUnsubscription = () => {
    if (pendingRewards.amount !== '0') {
      setStep(steps.promptUnsubscription.promptClaimReward);
    } else {
      setStep(steps.promptUnsubscription.confirm);
    }
  };

  const onAcceptClaimRewards = () => {
    setIsOpen(false);
    setIsClaimRewardOpen(true);
  };

  const onUnsubscribe = () => {
    if (!isOpen) {
      setIsOpen(true);
    }

    setStep(steps.promptClaimReward.confirm);
  };

  const onConnect = () => {
    proceedUnstake();
  };

  const onGoBack = () => {
    setStep('promptUnsubscription');
  };

  const unsubscribeWithdrawal = () => {
    if (isElectron()) {
      workerApi.setPendingUnsubscription(workerId).then(() => {
        unsubscriptionDelaySuccessNotification(workerId, withdrawalDelay);
        setStep(steps.executing.success);
        onFinishUnsubscriptionDelay();
      });
    } else {
      setStep(steps.executing.success);
      onFinishUnsubscriptionDelay();
    }
  };

  const proceedUnstake = async () => {
    try {
      setStep(steps.confirm.executing);
      await unstake(namespace, async ({ status, errorMsg, blockHash }) => {
        if (status === 'success') {
          const txUrl = `${walletConst.ewxExplorerUrl}/${blockHash}`;
          setTxUrl(txUrl);

          if (withdrawalDelay > 0) {
            unsubscribeWithdrawal();
          } else {
            if (isElectron()) {
              await workerApi.setStaked(workerId, 0, false);
              await workerApi.terminate(workerId);
            }

            setStep(steps.executing.success);
            unstakeSuccessNotification(workerId);
          }
        } else {
          setStep(steps.executing.failure);
          setErrorMsg(errorMsg ? formatPalletErrorMessage(errorMsg) : 'An unknown error occurred');
          unstakeErrorNotification(workerId);
        }
      });
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        setErrorMsg(error.message);
      } else if (typeof error === 'object' && error?.['message']) {
        setErrorMsg(error['message']);
      } else {
        setErrorMsg('An unknown error occurred');
      }
      setStep(steps.executing.failure);
      unstakeErrorNotification(workerId);
    }
  };

  const onFinish = () => {
    onClose();
    navigate(routerConst.Dashboard);
  };

  useEffect(() => {
    const checkFee = async () => {
      setFee(await workerApi.getFeeEWX('unstake'));
    };

    const checkRewards = async () => {
      const earnedRewardsData = await workerApi.getEarnedRewards(workerId);
      const earnedRewards = convertRewardAmount(earnedRewardsData.amount, earnedRewardsData.decimals);
      setPendingRewards({
        amount: earnedRewards || '0',
        symbol: earnedRewardsData.symbol,
        decimals: earnedRewardsData.decimals,
        isNative: earnedRewardsData.isNative,
      });

      if (earnedRewards && earnedRewards !== '0') {
        setStep('promptClaimReward');
      } else {
        setStep('promptUnsubscription');
      }
    };

    checkFee();
    checkRewards();
  }, [workerId]);

  const stepComponents: Record<Step, JSX.Element> = {
    promptUnsubscription: (
      <PromptUnsubscription
        onAccept={onAcceptUnsubscription}
        onClose={onClose}
        withdrawalDelay={withdrawalDelay}
      />
    ),
    promptClaimReward: (
      <PromptClaimReward
        onAccept={onAcceptClaimRewards}
        onUnsubscribe={onUnsubscribe}
        onClose={onClose}
      />
    ),
    confirm: (
      <Confirm
        fee={fee}
        address={addressEWX}
        stakedAmount={stakedAmount}
        onConfirm={onConfirm}
        onGoBack={onGoBack}
        onClose={onClose}
      />
    ),
    connect: <ConnectEWXWallet onConnect={onConnect} onClose={onClose} />,
    executing: <TxExecuting operation="unsubscribing" txStatus={status} />,
    success: (
      <TxSuccess operation="unsubscribing" txUrl={txUrl} onNext={onFinish} onClose={onFinish} />
    ),
    failure: <TxFailure operation="unsubscribing" onClose={onClose} message={errorMsg} />,
  };

  return (
    <>
      <Dialog.Root open={isOpen}>
        <Dialog.Portal>
          <Dialog.Overlay>
            <Dialog.Content>{stepComponents[step]}</Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
      <ClaimReward
        namespace={namespace}
        amount={pendingRewards.amount}
        symbol={pendingRewards.isNative ? getNativeTokenSymbol() : pendingRewards.symbol}
        decimals={pendingRewards.decimals}
        isNative={pendingRewards.isNative}
        isOpen={isClaimRewardOpen}
        setIsOpen={setIsClaimRewardOpen}
        onSuccess={onUnsubscribe}
        isUnsubscribe={true}
      />
    </>
  );
};

interface Props {
  workerId: WorkerApi.WorkerId;
  namespace: string;
  withdrawalDelay?: number;
  isOpen: boolean;
  setIsOpen: (newOpen: boolean) => void;
  onFinishUnsubscriptionDelay: () => void;
  stakedAmount?: string;
}
